import React, { useState, useEffect, useCallback } from 'react';
import config from "../../common/Config";
import { toast } from "sonner";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  IconButton,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Skeleton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';
import { postGetProductUnit, axiosPostProductUnitForm } from '../../../utils/productUnitApi';

const Units = () => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [totalUnits, setTotalUnits] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [menuAnchor, setMenuAnchor] = useState({ el: null, row: null });
  const [addUnitDialogOpen, setAddUnitDialogOpen] = useState(false);
  const [editUnitDialogOpen, setEditUnitDialogOpen] = useState(false);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem("token");  

  const [unitFormData, setUnitFormData] = useState({
    code: '',
    unitName: '',
  });

  const [unitEditData, setUnitEditData] = useState({
    code: '',
    unitName: '',
    uid: '',
  });

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const json = await postGetProductUnit(`${config.API_URL}/get_product_unit`, token, {
        draw: page + 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: searchValue || '',
      });

      if (json?.data && Array.isArray(json.data)) {
        setUnits(json.data);
        setTotalUnits(json.recordsTotal || json.recordsFiltered || json.data.length);
      } else {
        setUnits([]);
        setTotalUnits(0);
      }
    } catch {
      toast.error("Failed to fetch units");
      setUnits([]);
      setTotalUnits(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, accessToken, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setUnitFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUnitSubmit = async (event) => {
    event.preventDefault();

    if (!unitFormData.code || !unitFormData.unitName) {
      toast.error('Please fill all mandatory fields.');
      return;
    }

    const formData = new FormData();
    formData.append('unit_code', unitFormData.code);
    formData.append('unit_name', unitFormData.unitName);
    const apiBrandUrl = `${config.API_URL}/add_product_unit`;
    try {
      const data = await axiosPostProductUnitForm(apiBrandUrl, token, formData);
      const msg = data.message;
      if (data.success) {
        toast.success('Unit added successfully.');
        setAddUnitDialogOpen(false);
        setUnitFormData({
          code: '',
          unitName: '',
        });
        fetchData();
      } else {
        toast.error(msg || 'Failed to add unit');
      }
    } catch (error) {
      if (error?.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to add unit");
      }
    }
  };

  const handleEditClick = (rowData) => {
    setUnitEditData({
      code: rowData.code,
      unitName: rowData.name,
      uid: rowData.id,
    });
    setEditUnitDialogOpen(true);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor({ el: null, row: null });
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, row });
  };

  const handleMenuAction = (action, row) => {
    handleMenuClose();
    if (action === 'edit') {
      handleEditClick(row);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchValue(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleCloseAddDialog = () => {
    setAddUnitDialogOpen(false);
    setUnitFormData({
      code: '',
      unitName: '',
    });
  };

  const handleCloseEditDialog = () => {
    setEditUnitDialogOpen(false);
    setUnitEditData({
      code: '',
      unitName: '',
      uid: '',
    });
  };

  const handleFormEditChange = (e) => {
    const { name, value } = e.target;
    setUnitEditData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleUnitUpdate = async (event) => {
    event.preventDefault();

    if (!unitEditData.code || !unitEditData.unitName) {
      toast.error('Please fill all mandatory fields.');
      return;
    }

    const formData = new FormData();
    formData.append('unit_code', unitEditData.code);
    formData.append('unit_name', unitEditData.unitName);
    formData.append('uid', unitEditData.uid);
    const apiBrandEditUrl = `${config.API_URL}/edit_product_unit`;
    try {
      const data = await axiosPostProductUnitForm(apiBrandEditUrl, token, formData);
      const msg = data.message;
      if (data.success) {
        toast.success('Unit updated successfully.');
        setEditUnitDialogOpen(false);
        setUnitEditData({
          code: '',
          unitName: '',
          uid: '',
        });
        fetchData();
      } else {
        toast.error(msg || 'Failed to update unit');
      }
    } catch (error) {
      if (error?.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to update unit");
      }
    }
  };

  return (
    <Box>
      <Dialog
        open={addUnitDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Unit
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <Box component="form" id="addNewUnit" onSubmit={handleUnitSubmit} sx={{ pt: 2 }}>
            <Typography className='commonColor' variant="body2" sx={{ mb: 2}}>
              Please fill in the information below. The field labels marked with <span style={{ color: 'red' }}>*</span> are required input fields.
            </Typography>
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Unit Code"
                  name="code"
                  id="unitCode"
                  value={unitFormData.code}
                  onChange={handleFormChange}
                  required
                />
              </Grid2> 
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Unit Name"
                  name="unitName"
                  id="unitName"
                  value={unitFormData.unitName}
                  onChange={handleFormChange}
                  required
                />
              </Grid2>
            </Grid2>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAddDialog} 
          className='button-variant-outlined'
          variant='outlined'
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="addNewUnit"
            className="button-variant-contained"
          >
            Add Unit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editUnitDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Typography className="page-title">
            Edit Unit
          </Typography>
          <IconButton onClick={handleCloseEditDialog} sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <Box component="form" id="editUnit" onSubmit={handleUnitUpdate} sx={{ pt: 2 }}>
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                className='input-border'
                  fullWidth
                  size="small"
                  label="Unit Code"
                  name="code"
                  id="editUnitCode"
                  value={unitEditData.code}
                  onChange={handleFormEditChange}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                className='input-border'
                  fullWidth
                  size="small"
                  label="Unit Name"
                  name="unitName"
                  id="editUnitName"
                  value={unitEditData.unitName}
                  onChange={handleFormEditChange}
                  required
                />
              </Grid2>
            </Grid2>
            <input type="hidden" name="uid" value={unitEditData.uid} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEditDialog} 
          variant="outlined"
           className="button-variant-outlined" 
           sx={{ textTransform: 'none'}}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="editUnit"
            className="button-variant-contained"
          >
            Edit Unit
          </Button>
        </DialogActions>
      </Dialog>

      {/* <Header /> */}
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Typography className="page-title">
                  Units
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }} gap={1} display="flex" flexDirection={{ xs: "column", md: "row"}} justifyContent="flex-end" mt={{ xs: 2, md: 0 }}>
                <TextField
                  className="input-border"
                  size="small"
                  placeholder="Search units..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
                  sx={{
                    width: { xs: '100%', sm: 400 },
                  }}
                />
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={() => setAddUnitDialogOpen(true)}
                  startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                >
                  Add Unit
                </Button>
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer
          >
            <Table
              size="medium"
              className="table-cell-border"
              sx={{ tableLayout: "auto" }}
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  {["#", "Unit Code", "Unit Name", "Action"].map((header) => {
                    let align = "left";
                    if (header === "#" || header === "Action") {
                      align = "center";
                    }
                    return (
                      <TableCell
                        key={header}
                        align={align}
                        className="table-cell-header"
                      >
                        {header}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={4}>
                        <Skeleton height={40} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : units.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No records found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  units.map((row, index) => (
                    <TableRow
                      key={row.id}
                      hover
                      className="table-cell-style"
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(145, 158, 171, 0.04)"
                        },

                      }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="left">
                        {row.code}
                      </TableCell>
                      <TableCell align="left">
                        {row.name}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, row)}
                          sx={{
                            color: "#637381",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)"
                            }
                          }}
                        >
                          <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {units.length > 0 && (
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <TablePagination
                component="div"
                count={totalUnits}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                className="pagination-styles"
              />
            </Box>
          )}
        </Paper>
      </Box>
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          onClick: (e) => e.stopPropagation(),
          sx: {
            minWidth: 150,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 1,
          },
        }}
      >
        {menuAnchor.row && (
          <MenuItem onClick={() => handleMenuAction('edit', menuAnchor.row)}>
            <ListItemIcon>
              <Iconify icon="fa-regular:edit" width={20} height={20} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
      </Menu>

    </Box>
  );
};

export default Units;
